import { Order } from "@amzn/ito-client";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Row } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";
import { Text } from "@amzn/stencil-react-components/text";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import IconInformation from "@amzn/stencil-react-icons/icons/icon-information";
import { usePendingOrdersLink } from "common/components/ApprovalRequestsLink/hook";
import { APPROVED } from "common/types/orders-types";
import { getString } from "common/uistringlabels/uiStringUtils";
import { AMAZON_APPROVALS_URL } from "config";
import { UserPermissions } from "lib/services";
import React, { Dispatch, FunctionComponent, SetStateAction, useState } from "react";

export interface ApproveDenyButtonsProps {
  setSelectedOrder: Dispatch<SetStateAction<Order | undefined>>;
  selectedOrder?: Order;
  open: () => void;
  approveOrder: (order: Order) => void;
}

export const ApproveDenyButtons: FunctionComponent<ApproveDenyButtonsProps> = ({
  approveOrder,
  setSelectedOrder,
  selectedOrder,
  open,
}: ApproveDenyButtonsProps) => {
  const iAmApprover = UserPermissions.current.username === selectedOrder?.approver?.username;
  const [isApproving, setIsApproving] = useState(false);
  const { refreshPendingOrderCount } = usePendingOrdersLink();

  /* istanbul ignore next */
  const onApproveClick = async () => {
    setIsApproving(true);
    await approveOrder(selectedOrder!);
    refreshPendingOrderCount();
    setIsApproving(false);
  };

  /* istanbul ignore next */
  const onDeclineClick = () => {
    setSelectedOrder(selectedOrder);
    open();
  };

  const IconWithTooltip = withTooltip()(IconInformation);

  const needsHigherLevelManager = !iAmApprover || isApproving;

  const renderApprovalButtons = () => {
    return (
      <>
        {!iAmApprover && (
          <Row>
            <StatusIndicator
              messageText={`Must be approved by ${selectedOrder?.approver?.username}@`}
              status={Status.Unavailable}
            />
          </Row>
        )}
        <Row>
          <Button dataTestId="approve-button" disabled={needsHigherLevelManager} onClick={onApproveClick}>
            {getString("myOrdersApproval.approve")}{" "}
            {iAmApprover && isApproving && <Spinner size={SpinnerSize.Small}></Spinner>}
          </Button>
          <Button
            disabled={needsHigherLevelManager}
            dataTestId="decline-button"
            variant={ButtonVariant.Tertiary}
            onClick={onDeclineClick}
          >
            {getString("myOrdersApproval.decline")}
          </Button>
        </Row>
      </>
    );
  };

  const renderAmazonApprovalsList = () => {
    return (
      <>
        <Row alignItems="center" gridGap={8} dataTestId="amazon-approvals-text">
          <Text>{getString("myOrdersApproval.amazonApprovals")}</Text>
          <IconWithTooltip title="Show tooltip" tooltipText={getString("myOrdersApproval.amazonApprovalsTooltip")} />
        </Row>
        <Row>
          {selectedOrder
            ?.approvals!.filter((approval) => approval.approvalId !== "stp" && approval.status !== APPROVED)
            .map((approval, index, filteredApprovals) => (
              <React.Fragment key={approval.approvalId}>
                <Link href={`${AMAZON_APPROVALS_URL}/Approval/Details/${approval.approvalId}`} target="_blank">
                  {approval.approvalId}
                </Link>
                {index < filteredApprovals.length - 1 && ", "}
              </React.Fragment>
            ))}
        </Row>
      </>
    );
  };

  return (
    <div>
      {selectedOrder?.approver?.username &&
        selectedOrder?.approvals?.some((item) => item.templateId === "stp" && item.status !== APPROVED) &&
        renderApprovalButtons()}
      {selectedOrder?.approvals?.some((item) => item.templateId !== "stp") &&
        selectedOrder?.approvals?.filter((approval) => approval.approvalId !== "stp" && approval.status !== APPROVED)
          .length > 0 &&
        renderAmazonApprovalsList()}
    </div>
  );
};
